import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation, useSetPage } from '@app/hooks';
import { resetState, setOrderByDirection, setOrderByProperty, resetScroll, setExceptionHandler } from '@app/store/actions';
import {
    getMenuItemById,
    infiniteScrollItemsFetchLimit,
    genericErrorToast,
    hasOrgPermission,
    provisionStateSelectData
} from '@app/helpers';
import { ITableHead, IListPageBarEnabled, IAgentDetail, IFabMenu, IActiveFilter, FilterEnum, IDotMenu, OrderType, Order } from '@app/models';
import { ManageVault } from '@app/store/constants';
import { FabMenu, mainMenuTypes, IMenuItem, PasslyTable } from '@app/components';
import { basicService, vaultPasswordsService } from '@app/services';
import { confirm } from '@app/components/Modal/confirmModal';

interface IAgentDetailTableData {
    checked: boolean;
    agentName: string;
    lastModified: Date;
    provisioningState: string;
    clientId: string;
    id: string;
}

enum TableHeadEnum {
    agentName = 'agentName',
    lastModified = 'lastModified',
    provisioningState = 'provisioningState',
    thirdParty = 'clientId'
}

const AgentsPage = () => {
    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.Agents);
    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState('AgentName');
    const [rows, setRows] = useState<IAgentDetailTableData[]>([]);
    const [menuItem] = useState<IMenuItem | undefined>(item);
    const [loadingData, setLoadingData] = useState<boolean>(true);
    const activeFilters: IActiveFilter[] = useSelector((state: any) => state.listPageBar.activeFilters);
    const organizationFilter = useSelector((state: any) => state.organizationSelect.selectedOrganization);
    const [agentTotalCount, setAgentTotalCount] = useState(0);
    const [orgCanView, setOrgCanView] = useState<boolean>(true);

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: true,
        organizationsEnabled: true,
        actionMenuEnabled: true
    };

    useSetPage(setListPageBarEnabledOptions, menuItem);

    useEffect(() => {
        dispatch(setExceptionHandler(true));
    }, []);

    const createSortHandler = (property: string) => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;
        dispatch(resetState());
        setOrder(ascDesc);
        setOrderBy(property);
        dispatch(setOrderByDirection(ascDesc));
        dispatch(setOrderByProperty(property));
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        setLoadingData(true);

        vaultPasswordsService.getAgents(
            orderBy,
            order,
            infiniteScrollItemsFetchLimit,
            skipCount,
            organizationFilter,
            handleError,
            handleSuccess,
            isReset,
            activeFilters
        );
    };

    const handleError = (error: any) => {
        const respStatus = error.response.status;
        const errorMessage = error.response.data.error.message;

        setRows([]);
        setAgentTotalCount(0);
        setLoadingData(false);

        if (respStatus === 400 && errorMessage === 'ORG_CANNOT_VIEW') {
            setOrgCanView(false);
            return;
        }

        setOrgCanView(true);
        genericErrorToast(error.response);
    };

    const handleSuccess = (response: any) => {
        const count = response.data['@odata.count'];
        setOrgCanView(true);

        if (count !== undefined) {
            setAgentTotalCount(count);
        }

        const results: IAgentDetail[] = response.data.value;

        if (results) {
            let newRows = results.map((row: IAgentDetail) => {
                let thirdParty: string = translation('NO');

                if (row.ClientId.toLowerCase().includes('thirdparty')) {
                    thirdParty = translation('YES');
                }

                const data: IAgentDetailTableData = {
                    checked: false,
                    id: row.Id,
                    agentName: row.AgentName,
                    lastModified: row.LastModified,
                    provisioningState: row.ProvisioningState,
                    clientId: thirdParty
                };
                return data;
            });

            if (results.length === 0) {
                setRows([]);
            } else if (count !== undefined) {
                setRows(newRows);
            } else {
                setRows(rows.concat(newRows));
            }
        }

        setLoadingData(false);
    };

    const addNewAgent = () => {
        basicService.redirect('/passwordserver/agents/onboarding');
    };

    const emptyList = () => {
        return !loadingData && rows.length === 0;
    };

    const editRow = (e: any) => {
        basicService.redirect('/passwordserver/agents/details/' + e.currentTarget.dataset.id);
    };

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.agentName,
            label: 'AGENT_NAME',
            type: 'link',
            onClick: editRow,
            filterType: FilterEnum.contains,
            defaultSearchBy: TableHeadEnum.agentName,
        },
        {
            id: TableHeadEnum.lastModified,
            label: 'AGENT_LAST_CHECKIN',
            type: 'dateTime'
        },
        {
            id: TableHeadEnum.provisioningState,
            label: 'AGENT_STATE',
            type: 'status',
            filterType: FilterEnum.provisioningState,
            suggestionsForFilter: provisionStateSelectData
        },
        {
            id: TableHeadEnum.thirdParty,
            label: 'THIRD_PARTY'
        }
    ];

    const menu: IFabMenu = {
        name: 'NEW_AGENT',
        actions: [
            {
                name: 'NEW_AGENT',
                method: () => addNewAgent(),
                iconElement: <i className='icon icon-directory-sync' />
            }
        ]
    };

    const deleteSelected = useCallback(() => {
        // loop thru rows.checked
        let ids: string[] = [];

        rows.forEach(function (row) {
            if (row.checked) {
                ids.push(row.id);
            }
        });

        if (ids.length === 0) {
            confirm(translation('SELECT_MORE'), translation('OK'), undefined, '').then(
                () => {
                    // do nothing
                },
                () => {
                    // do nothing on cancel
                }
            );
            return;
        }

        deleteAgentAreYouSure(ids);
    }, [rows]);

    const deleteAgentAreYouSure = (ids: any) => {
        confirm(translation('DELETE_AGENT_BODY'), translation('DELETE'), translation('CANCEL'), translation('DELETE_AGENT_HEADER'), false, {
            isDeleteAction: true
        }).then(
            () => {
                deleteAgents(ids);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const deleteAgents = (ids: string[]) => {
        if (ids.length > 0) {
            let agents: any = {
                PwSSyncAgentProfiles: ids
            };

            vaultPasswordsService.deleteAgent(agents, genericErrorToast, handleDeleteSuccess);
        }
    };

    const handleDeleteSuccess = () => {
        handlePageRefreshFromDialog(true);
    };

    const handlePageRefreshFromDialog = (refreshPage: boolean) => {
        if (refreshPage) {
            setRows([]);
            dispatch(resetScroll());
        }
    };

    const deleteRow = (e: any) => {
        let ids: string[] = [];
        ids.push(e.currentTarget.dataset.id);

        deleteAgentAreYouSure(ids);
    };

    const actionMenuItems: IDotMenu[] = [
        {
            name: translation('DELETE_SELECTED'),
            action: deleteSelected,
            isDeleteAction: true
        }
    ];

    return (
        <>
            {hasOrgPermission([ManageVault]) ? (
                <>
                    <PasslyTable
                        rows={rows}
                        setRows={setRows}
                        order={order}
                        orderBy={orderBy}
                        handleOrderChange={createSortHandler}
                        headerItems={headCells}
                        rowsCount={agentTotalCount}
                        onFetch={fetchMoreData}
                        withCheckboxes={true}
                        actionMenuItems={actionMenuItems}
                        emptyListHelper={translation('EMPTY_AGENT_LIST')}
                        loadingData={loadingData}
                        editRow={editRow}
                        deleteRow={deleteRow}
                    />

                    {!loadingData && orgCanView && (
                        <FabMenu
                            menu={menu}
                            hideTooltip={!emptyList()}
                            tooltipTitle={translation('PWS_SYNCAGENTS_NO_AGENTS_TOOL_TIP_TITLE')}
                            tooltipDescription={translation('PWS_SYNCAGENTS_NO_AGENTS_TOOL_TIP_DESC')}
                        />
                    )}
                </>
            ) : (
                <div className='passwordVaults__no-results'> {translation('NO_ORG_SYNC_PERMISSIONS')}</div>
            )}
        </>
    );
};

export default AgentsPage;
